import React from "react";
import {
  getTabScopedSupabaseStorageKey,
  getSupabaseSession,
  isSupabaseConfigured,
  signOutSupabaseSession,
} from "../lib/supabaseClient.js";
import {
  createInactivityTimeoutController,
} from "../utils/inactivityTimeout.js";

const AUTHENTICATED_ACTIVITY_CHANNEL_PREFIX = "grain:authenticated-activity";
const AUTHENTICATED_ACTIVITY_MESSAGE_TYPE = "activity";
const AUTHENTICATED_ACTIVITY_BROADCAST_THROTTLE_MS = 5_000;

export default function useSupabaseInactivityTimeout({ enabled = true, onLoggedOut } = {}) {
  const onLoggedOutRef = React.useRef(onLoggedOut);

  React.useEffect(() => {
    onLoggedOutRef.current = onLoggedOut;
  }, [onLoggedOut]);

  React.useEffect(() => {
    if (!enabled || typeof window === "undefined" || !isSupabaseConfigured()) {
      return undefined;
    }

    let isMounted = true;
    let signOutInProgress = false;
    let activityChannel = null;
    let lastActivityBroadcastAt = 0;
    const BroadcastChannelConstructor = window.BroadcastChannel;
    if (typeof BroadcastChannelConstructor === "function") {
      try {
        activityChannel = new BroadcastChannelConstructor(
          `${AUTHENTICATED_ACTIVITY_CHANNEL_PREFIX}:${getTabScopedSupabaseStorageKey()}`,
        );
      } catch {}
    }

    const inactivityController = createInactivityTimeoutController({
      eventTarget: window,
      onActivity: () => {
        if (!activityChannel) {
          return;
        }

        const now = Date.now();
        if (now - lastActivityBroadcastAt < AUTHENTICATED_ACTIVITY_BROADCAST_THROTTLE_MS) {
          return;
        }

        lastActivityBroadcastAt = now;
        try {
          activityChannel.postMessage({ type: AUTHENTICATED_ACTIVITY_MESSAGE_TYPE });
        } catch {}
      },
      onInactive: async () => {
        if (signOutInProgress) {
          return;
        }

        signOutInProgress = true;
        const { error } = await signOutSupabaseSession({ scope: "local" });
        const { session } = await getSupabaseSession();

        if (!isMounted) {
          return;
        }

        if (session) {
          signOutInProgress = false;
          if (error) {
            return;
          }
          return;
        }

        onLoggedOutRef.current?.();
      },
    });
    const handleRemoteActivity = (event) => {
      if (event?.data?.type === AUTHENTICATED_ACTIVITY_MESSAGE_TYPE) {
        inactivityController.signalActivity();
      }
    };

    activityChannel?.addEventListener("message", handleRemoteActivity);
    inactivityController.start();

    return () => {
      isMounted = false;
      signOutInProgress = false;
      activityChannel?.removeEventListener("message", handleRemoteActivity);
      activityChannel?.close();
      inactivityController.stop();
    };
  }, [enabled]);
}
